/**
 * Фича «Двигать элементы плана»: перетаскивание углов, стен целиком, отдельно стоящей
 * комнаты со всем, что в ней (мебель, светильники, трассы кабелей и труб, комментарии),
 * проёмов и розеток вдоль стены, предметов по полу и светильников по потолку,
 * комментариев (рамка и конец стрелки двигаются по отдельности),
 * размер предмета на полу ручками на его сторонах, а также панорамирование холста.
 *
 * Размер меняется от стороны: тянешь правый край — левый стоит, тянешь переднюю
 * сторону — задняя остаётся у стены (`resizeFloorItem`). Пределы и шаг — из каталога.
 * Пока тянешь, `sizeHint` говорит холсту, какой размер подписать у ручки.
 *
 * Все жесты идут через один ref-объект состояния перетаскивания, поэтому
 * холсту достаточно прокинуть три обработчика: move / up / начало нужного жеста.
 * Историю пишем один раз — при первом сдвиге жеста: иначе undo откатывал бы по пикселю,
 * а простой клик для выделения оставлял бы в истории пустой шаг.
 */
import { useRef, useState } from "react";
import type { PointerEvent } from "react";
import {
	dist,
	leftNormal,
	norm,
	pointInPolygon,
	projectOnSegment,
	rotateVector,
	snapTo,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import {
	bendCableSegment,
	findFixture,
	findFloorItem,
	findWallConflict,
	resizeFloorItem,
	sizeTowardsPoint,
	snapPoint,
	type FloorItem,
	type RoomFloor,
	type ResizeAnchor,
	type Wall,
	type WallGraph,
	type SnapGuides,
} from "@/shared/plan";
import { useEditorStore, useProjectStore } from "@/shared/store";
import {
	useWallsStore,
	useFloorsStore,
	useFixturesStore,
	useCommentsStore,
	usePipesStore,
	useWiringStore,
	wallFixtureOwner,
	floorItemOwner,
} from "@/shared/store/layers";
import { getCatalogItem } from "@/shared/catalog";

type DragState =
	| { kind: "pan"; lastX: number; lastY: number }
	| { kind: "node"; id: string }
	| { kind: "wall"; id: string; grabbed: Pt; origin: Record<string, Pt> }
	| { kind: "room"; grabbed: Pt; origin: RoomContents; applied: Pt }
	| { kind: "floor-pattern"; roomId: string; floor: RoomFloor; grabbed: Pt; origin: Pt }
	| { kind: "opening"; id: string }
	| { kind: "fixture"; id: string }
	| { kind: "cable"; id: string; route: Pt[]; segment: number; grabbed: Pt }
	| { kind: "item"; id: string; grabbed: Pt; origin: Pt }
	| { kind: "rotate"; id: string; center: Pt }
	| { kind: "resize"; id: string; axis: "width" | "depth"; anchor: ResizeAnchor; origin: FloorItem }
	| { kind: "comment"; id: string; part: "box" | "anchor"; grabbed: Pt; origin: Pt }
	| null;

/** Что едет вместе с комнатой: положения на момент, когда её взяли. */
interface RoomContents {
	nodes: Record<string, Pt>;
	floorItems: Record<string, Pt>;
	ceilingItems: Record<string, Pt>;
	comments: Record<string, { anchor: Pt; box: Pt }>;
	/** Трассы: точки внутри комнаты едут, снаружи — стоят */
	cables: Record<string, { points: Pt[]; inside: boolean[] }>;
	pipes: Record<string, { points: Pt[]; inside: boolean[] }>;
}

/** Комната, которую тащат: её узлы и чистый контур (что внутри — едет с ней). */
export interface RoomDragTarget {
	nodeIds: string[];
	inner: Pt[];
}

/** Подпись размера у ручки, пока его тянут. */
export interface SizeHint {
	id: string;
	axis: "width" | "depth";
	/** Новый размер, мм */
	value: number;
}

const ROTATE_STEP = 15;

function nearestWall(plan: WallGraph, point: Pt) {
	let best: {
		wall: Wall;
		a: Pt;
		b: Pt;
		projection: ReturnType<typeof projectOnSegment>;
	} | null = null;
	for (const wall of Object.values(plan.walls)) {
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b || dist(a, b) < 1) continue;
		const projection = projectOnSegment(point, a, b);
		if (!best || projection.distance < best.projection.distance) best = { wall, a, b, projection };
	}
	return best;
}

interface Params {
	toWorld: (clientX: number, clientY: number) => Pt;
	panByPixels: (dxPx: number, dyPx: number) => void;
	mmPerPx: () => number;
	/**
	 * Куда встанет перетаскиваемый предмет на стене под курсором: правило «предмет живёт
	 * в своей комнате» знает холст (комнаты считаются в `widgets/plan`). null — правило
	 * не применимо, предмет едет на ближайшую стену.
	 */
	wallSpot?: (
		fixtureId: string,
		point: Pt,
		free: boolean,
	) => { wallId: string; side: 1 | -1; offset: number } | null;
}

const capture = (e: PointerEvent<Element>) => {
	const target = e.target as Element & { setPointerCapture?: (id: number) => void };
	try {
		target.setPointerCapture?.(e.pointerId);
	} catch {
		// указателя уже нет
	}
};

/** «Красивые» шаги сдвига стены, мм. */
const FINE_STEPS = [1, 2, 5, 10, 20, 50, 100];

/**
 * Шаг сдвига стены мышью: наименьший «красивый» шаг, не мельче одного пикселя экрана, —
 * и не крупнее сетки. Мельче пикселя мышью всё равно не попасть, крупнее — стена прыгает.
 */
const fineStep = (mmPerPx: number, gridStep: number): number =>
	Math.min(gridStep, FINE_STEPS.find((step) => step >= mmPerPx) ?? gridStep);

export const useTransformPlan = ({ toWorld, panByPixels, mmPerPx, wallSpot }: Params) => {
	const drag = useRef<DragState>(null);
	/**
	 * Жест начат, но история ещё не записана: пишем при первом реальном сдвиге. Клик,
	 * которым просто выделяют предмет, не должен оставлять пустой шаг в Ctrl+Z.
	 */
	const needsHistory = useRef(false);
	const gridStep = useEditorStore((s) => s.gridStep);
	const projectStore = useProjectStore();
	const wallsStore = useWallsStore();
	const floorsStore = useFloorsStore();
	const fixturesStore = useFixturesStore();

	const [guides, setGuides] = useState<SnapGuides>({});
	const [sizeHint, setSizeHint] = useState<SizeHint | null>(null);

	const beginPan = (e: PointerEvent<Element>) => {
		drag.current = { kind: "pan", lastX: e.clientX, lastY: e.clientY };
		capture(e);
	};

	const beginNodeDrag = (e: PointerEvent<Element>, id: string) => {
		needsHistory.current = true;
		drag.current = { kind: "node", id };
		capture(e);
	};

	const beginWallDrag = (e: PointerEvent<Element>, id: string) => {
		const plan = projectStore.project.layers.walls;
		const wall = plan.walls[id];
		if (!wall) return;
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) return;
		needsHistory.current = true;
		drag.current = {
			kind: "wall",
			id,
			grabbed: toWorld(e.clientX, e.clientY),
			origin: { [wall.a]: { x: a.x, y: a.y }, [wall.b]: { x: b.x, y: b.y } },
		};
		capture(e);
	};

	/** Начать тянуть комнату целиком — вместе с тем, что стоит и висит внутри неё. */
	const beginRoomDrag = (e: PointerEvent<Element>, room: RoomDragTarget) => {
		const layers = projectStore.project.layers;
		const inside = (p: Pt) => pointInPolygon(p, room.inner);
		const pick = (items: Record<string, { x: number; y: number }>) =>
			Object.fromEntries(
				Object.entries(items)
					.filter(([, item]) => inside(item))
					.map(([id, item]) => [id, { x: item.x, y: item.y }]),
			);
		const routes = (items: Record<string, { points: Pt[] }>) =>
			Object.fromEntries(
				Object.entries(items)
					.map(
						([id, item]) => [id, { points: item.points, inside: item.points.map(inside) }] as const,
					)
					.filter(([, route]) => route.inside.some(Boolean)),
			);
		const nodes: Record<string, Pt> = {};
		for (const id of room.nodeIds) {
			const node = layers.walls.nodes[id];
			if (node) nodes[id] = { x: node.x, y: node.y };
		}
		needsHistory.current = true;
		drag.current = {
			kind: "room",
			grabbed: toWorld(e.clientX, e.clientY),
			applied: { x: 0, y: 0 },
			origin: {
				nodes,
				floorItems: { ...pick(layers.furniture.items), ...pick(layers.plumbing.items) },
				ceilingItems: pick(layers.electrical.items),
				// Привязанный комментарий едет за своим объектом сам — двигаем только свободные
				comments: Object.fromEntries(
					Object.values(useCommentsStore.getState().items)
						.filter((comment) => !comment.target && inside(comment.anchor))
						.map((comment) => [comment.id, { anchor: comment.anchor, box: comment.box }]),
				),
				cables: routes(layers.wiring.items),
				pipes: routes(layers.pipes.items),
			},
		};
		capture(e);
	};

	/** Начать сдвиг раскладки пола, не двигая стены и предметы комнаты. */
	const beginFloorPatternDrag = (e: PointerEvent<Element>, roomId: string, floor: RoomFloor) => {
		needsHistory.current = true;
		drag.current = {
			kind: "floor-pattern",
			roomId,
			floor,
			grabbed: toWorld(e.clientX, e.clientY),
			origin: { x: floor.offsetX ?? 0, y: floor.offsetY ?? 0 },
		};
		capture(e);
	};

	/**
	 * Сдвинуть комнату на `delta` от исходного положения. Не сдвигает, если её стены
	 * пересекли бы чужие или легли на них, — комната упирается, как предмет в стену.
	 * @returns получилось ли
	 */
	const moveRoom = (origin: RoomContents, delta: Pt): boolean => {
		const plan = projectStore.project.layers.walls;
		const shift = (p: Pt) => ({ x: p.x + delta.x, y: p.y + delta.y });
		const nodes = { ...plan.nodes };
		for (const [id, p] of Object.entries(origin.nodes)) nodes[id] = { ...nodes[id], ...shift(p) };
		const moved = { ...plan, nodes };
		for (const wall of Object.values(plan.walls)) {
			if (!origin.nodes[wall.a] && !origin.nodes[wall.b]) continue;
			const others = { ...moved.walls };
			delete others[wall.id];
			const conflict = findWallConflict({ ...moved, walls: others }, nodes[wall.a], nodes[wall.b], {
				thickness: wall.thickness,
				nodeTolerance: 1,
				edgeTolerance: 1,
			});
			if (conflict) return false;
		}
		wallsStore.restoreWalls(moved);
		for (const [id, p] of Object.entries(origin.floorItems)) {
			const to = shift(p);
			floorItemOwner(id)?.getState().setFloorItemPosition(id, to.x, to.y);
		}
		for (const [id, p] of Object.entries(origin.ceilingItems)) {
			const to = shift(p);
			fixturesStore.setCeilingItemPosition(id, to.x, to.y);
		}
		const comments = useCommentsStore.getState();
		for (const [id, c] of Object.entries(origin.comments)) {
			comments.updateComment(id, { anchor: shift(c.anchor), box: shift(c.box) });
		}
		const routePoints = (route: { points: Pt[]; inside: boolean[] }) =>
			route.points.map((p, i) => (route.inside[i] ? shift(p) : p));
		const wiring = useWiringStore.getState();
		for (const [id, route] of Object.entries(origin.cables)) {
			wiring.updateCable(id, { points: routePoints(route) });
		}
		const pipes = usePipesStore.getState();
		for (const [id, route] of Object.entries(origin.pipes)) {
			pipes.updatePipe(id, { points: routePoints(route) });
		}
		return true;
	};

	const beginOpeningDrag = (e: PointerEvent<Element>, id: string) => {
		needsHistory.current = true;
		drag.current = { kind: "opening", id };
		capture(e);
	};

	const beginFixtureDrag = (e: PointerEvent<Element>, id: string) => {
		needsHistory.current = true;
		drag.current = { kind: "fixture", id };
		capture(e);
	};

	const beginCableDrag = (e: PointerEvent<Element>, id: string, route: Pt[]) => {
		const grabbed = toWorld(e.clientX, e.clientY);
		let segment = -1;
		let nearest = Infinity;
		for (let index = 0; index < route.length - 1; index++) {
			if (dist(route[index], route[index + 1]) < 1) continue;
			const projection = projectOnSegment(grabbed, route[index], route[index + 1]);
			if (projection.distance < nearest) {
				nearest = projection.distance;
				segment = index;
			}
		}
		if (segment < 0) return;
		needsHistory.current = true;
		drag.current = { kind: "cable", id, route, segment, grabbed };
		capture(e);
	};

	const beginItemDrag = (e: PointerEvent<Element>, id: string) => {
		const found = findFloorItem(projectStore.project.layers, id);
		if (!found) return;
		needsHistory.current = true;
		drag.current = {
			kind: "item",
			id,
			grabbed: toWorld(e.clientX, e.clientY),
			origin: { x: found.item.x, y: found.item.y },
		};
		capture(e);
	};

	const beginItemRotate = (e: PointerEvent<Element>, id: string) => {
		const found = findFloorItem(projectStore.project.layers, id);
		if (!found) return;
		needsHistory.current = true;
		drag.current = { kind: "rotate", id, center: { x: found.item.x, y: found.item.y } };
		capture(e);
	};

	/**
	 * Начать тянуть сторону предмета. `anchor` — сторона, которая стоит: у ручки справа
	 * стоит левый край (`start`), у ручки слева — правый (`end`); так же по глубине.
	 */
	const beginItemResize = (
		e: PointerEvent<Element>,
		id: string,
		axis: "width" | "depth",
		anchor: ResizeAnchor,
	) => {
		const found = findFloorItem(projectStore.project.layers, id);
		if (!found) return;
		needsHistory.current = true;
		drag.current = { kind: "resize", id, axis, anchor, origin: { ...found.item } };
		capture(e);
	};

	/** Начать тянуть комментарий: за рамку — едет рамка, за ручку на стрелке — её конец. */
	const beginCommentDrag = (e: PointerEvent<Element>, id: string, part: "box" | "anchor") => {
		const comment = useCommentsStore.getState().items[id];
		if (!comment) return;
		needsHistory.current = true;
		drag.current = {
			kind: "comment",
			id,
			part,
			grabbed: toWorld(e.clientX, e.clientY),
			origin: part === "box" ? comment.box : comment.anchor,
		};
		capture(e);
	};

	const handlePointerMove = (e: PointerEvent<SVGSVGElement>): boolean => {
		const state = drag.current;
		if (!state) return false;

		if (state.kind === "pan") {
			panByPixels(e.clientX - state.lastX, e.clientY - state.lastY);
			drag.current = { ...state, lastX: e.clientX, lastY: e.clientY };
			return true;
		}

		const world = toWorld(e.clientX, e.clientY);
		if (state.kind === "cable") {
			const start = state.route[state.segment];
			const end = state.route[state.segment + 1];
			const horizontal = Math.abs(end.x - start.x) >= Math.abs(end.y - start.y);
			const raw = horizontal ? world.y - state.grabbed.y : world.x - state.grabbed.x;
			const step = fineStep(mmPerPx(), gridStep);
			const offset = e.altKey ? raw : snapTo(raw, step);
			if (offset === 0 && needsHistory.current) return true;
			if (needsHistory.current) {
				needsHistory.current = false;
				projectStore.pushHistory();
			}
			useWiringStore.getState().updateCable(state.id, {
				points: bendCableSegment(state.route, state.segment, offset),
			});
			return true;
		}
		if (state.kind === "floor-pattern") {
			const delta = sub(world, state.grabbed);
			if (delta.x === 0 && delta.y === 0) return true;
			const patternDelta = rotateVector(delta, -((state.floor.rotation ?? 0) * Math.PI) / 180);
			if (needsHistory.current) {
				needsHistory.current = false;
				projectStore.pushHistory();
			}
			floorsStore.setRoomFloorMaterial(state.roomId, state.floor.material, state.floor.layout, {
				offsetX: state.origin.x + patternDelta.x,
				offsetY: state.origin.y + patternDelta.y,
			});
			return true;
		}

		if (needsHistory.current) {
			needsHistory.current = false;
			projectStore.pushHistory();
		}

		const layers = projectStore.project.layers;
		const plan = layers.walls;

		if (state.kind === "node") {
			const snap = snapPoint(plan, world, {
				gridStep,
				nodeTolerance: 10 * mmPerPx(),
				exclude: state.id,
				freeform: e.altKey,
			});
			wallsStore.moveNode(state.id, snap.point.x, snap.point.y);
			setGuides(snap.guides);
			return true;
		}

		if (state.kind === "wall") {
			const wall = plan.walls[state.id];
			if (!wall) return true;
			const raw = sub(world, state.grabbed);
			// Шаг — от размера пикселя на экране, а не сетка: приблизил — стена ходит по миллиметру,
			// отдалил — по сантиметрам; Alt — без шага
			const step = fineStep(mmPerPx(), gridStep);
			const delta = e.altKey ? raw : { x: snapTo(raw.x, step), y: snapTo(raw.y, step) };
			for (const nodeId of [wall.a, wall.b]) {
				const origin = state.origin[nodeId];
				if (origin) wallsStore.moveNode(nodeId, origin.x + delta.x, origin.y + delta.y);
			}
			return true;
		}

		if (state.kind === "room") {
			const raw = sub(world, state.grabbed);
			const delta = e.altKey ? raw : { x: snapTo(raw.x, gridStep), y: snapTo(raw.y, gridStep) };
			if (delta.x === state.applied.x && delta.y === state.applied.y) return true;
			// Упёрлась — пробуем проехать хотя бы по одной оси, вдоль препятствия
			const tries = [delta, { x: delta.x, y: state.applied.y }, { x: state.applied.x, y: delta.y }];
			const done = tries.find((attempt) => moveRoom(state.origin, attempt));
			if (done) drag.current = { ...state, applied: done };
			return true;
		}

		if (state.kind === "rotate") {
			const v = sub(world, state.center);
			const raw = (Math.atan2(v.y, v.x) * 180) / Math.PI + 90;
			floorItemOwner(state.id)
				?.getState()
				.setFloorItemRotation(state.id, e.altKey ? raw : snapTo(raw, ROTATE_STEP));
			return true;
		}

		if (state.kind === "resize") {
			const { origin, axis, anchor } = state;
			const info = getCatalogItem(origin.ref);
			if (!info) return true;
			const raw = sizeTowardsPoint(origin, info, axis, anchor, world);
			// Растущий предмет упирается в стены — кровать не уходит за стену
			const patch = resizeFloorItem(origin, info, axis, raw, anchor, plan);
			floorItemOwner(state.id)?.getState().updateFloorItem(state.id, patch);
			setSizeHint({ id: state.id, axis, value: patch[axis] ?? info[axis] });
			return true;
		}

		if (state.kind === "comment") {
			// Без привязки к сетке: комментарий ставят «на глаз» к тому, на что он указывает
			const delta = sub(world, state.grabbed);
			const point = { x: state.origin.x + delta.x, y: state.origin.y + delta.y };
			useCommentsStore.getState().updateComment(state.id, { [state.part]: point });
			return true;
		}

		if (state.kind === "item") {
			const raw = sub(world, state.grabbed);
			const delta = e.altKey ? raw : { x: snapTo(raw.x, gridStep), y: snapTo(raw.y, gridStep) };
			const x = state.origin.x + delta.x;
			const y = state.origin.y + delta.y;
			// Светильник на потолке лежит в слое электрики, предмет на полу — в мебели или сантехнике
			if (fixturesStore.ceilingItems[state.id])
				fixturesStore.setCeilingItemPosition(state.id, x, y);
			else floorItemOwner(state.id)?.getState().setFloorItemPosition(state.id, x, y);
			return true;
		}

		if (state.kind === "fixture") {
			const fixture = findFixture(layers, state.id)?.fixture;
			if (!fixture) return true;
			// По чистой грани помещения под курсором
			const spot = wallSpot?.(state.id, world, e.altKey);
			if (spot) {
				if (fixturesStore.items[state.id]) {
					fixturesStore.setFixtureWall(state.id, spot.wallId, spot.offset, spot.side);
				} else {
					wallFixtureOwner(state.id)
						?.getState()
						.setFixtureWall(state.id, spot.wallId, spot.offset, spot.side);
				}
				return true;
			}
			const target = nearestWall(plan, world);
			if (!target) return true;
			const { wall, a, b, projection } = target;
			const along = norm(sub(b, a));
			const normal = leftNormal(along);
			const away = sub(world, projection.point);
			const side: 1 | -1 = away.x * normal.x + away.y * normal.y >= 0 ? 1 : -1;
			// Check which store owns this fixture
			const electricalFixture = fixturesStore.items[state.id];
			if (electricalFixture) {
				fixturesStore.setFixtureWall(
					state.id,
					wall.id,
					snapTo(projection.t * dist(a, b), 10),
					side,
				);
			} else {
				// Сантехника или настенная мебель — у каждой свой стор
				wallFixtureOwner(state.id)
					?.getState()
					.setFixtureWall(state.id, wall.id, snapTo(projection.t * dist(a, b), 10), side);
			}
			return true;
		}

		const opening = plan.openings[state.id];
		const wallObj = opening ? plan.walls[opening.wallId] : null;
		if (!opening || !wallObj) return true;
		const a = plan.nodes[wallObj.a];
		const b = plan.nodes[wallObj.b];
		if (!a || !b) return true;
		const projection = projectOnSegment(world, a, b);
		wallsStore.setOpeningOffset(state.id, snapTo(projection.t * dist(a, b), 10));
		return true;
	};

	const handlePointerUp = (e: PointerEvent<Element>) => {
		setGuides({});
		setSizeHint(null);
		needsHistory.current = false;
		const state = drag.current;
		if (!state) return;
		// Захват мог быть на другом указателе (второй палец жеста) или уже снят системой — не падаем
		const target = e.target as Element & {
			hasPointerCapture?: (id: number) => boolean;
			releasePointerCapture?: (id: number) => void;
		};
		try {
			if (target.hasPointerCapture?.(e.pointerId)) target.releasePointerCapture?.(e.pointerId);
		} catch {
			// указателя уже нет
		}
		drag.current = null;
	};

	return {
		guides,
		sizeHint,
		beginPan,
		beginNodeDrag,
		beginWallDrag,
		beginRoomDrag,
		beginFloorPatternDrag,
		beginOpeningDrag,
		beginFixtureDrag,
		beginCableDrag,
		beginItemDrag,
		beginItemRotate,
		beginItemResize,
		beginCommentDrag,
		handlePointerMove,
		handlePointerUp,
		isDragging: () => drag.current !== null,
	};
};
